import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { BaseCompanyIdProps, TeamId } from "@custom-types/base-types";
import { isGroupRole } from "@custom-types/member-type-guards";
import { Dialog } from "@flat-ui";
import { useRespondToInvite } from "@hooks/use-respond-to-invite";
import { useToast } from "@hooks/use-toast";
import { CreateTeamDialogContent } from "@pages/members/teams/create-team-dialog-content";
import { reducerTeams } from "@pages/members/teams/create-team-reducer";
import { shouldDisableSendInvite } from "@pages/members/teams/teams-utils";
import { TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { updateMemberRoleInGroup } from "@store/groups/groups-slice-thunk";
import { setOne } from "@store/members/members-slice";
import { updateMemberRoleInWorkspace } from "@store/members/members-slice-thunk";
import { convertToTeamMemberTypes } from "@store/members/members-slice-utils";
import { useAppDispatch } from "@store/store-helper";
import { addMembersToTeam, createTeam } from "@store/teams/teams-slice-thunk";
import { nounPluralize } from "@utils/data-display";
import { capitalizeFirstLetter } from "@utils/string-utils";
import { isValidTeamName } from "@utils/team-utils";
import { TeamEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useReducer } from "react";

interface Props extends BaseCompanyIdProps {
  /** If the dialog it's opened or not  */
  isDialogOpen: boolean;

  /** Close the dialog */
  onCloseDialog: () => void;
}

/** The max number of characters for the input name */
export const MAX_NAME_CHARACTERS = 50;

/** The max number of characters for the input description */
export const MAX_DESCRIPTION_CHARACTERS = 500;

/** The dialog for the create team */
export function CreateTeamDialog({
  companyId,
  isDialogOpen,
  onCloseDialog,
}: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const { handleErrorWithToast } = useErrorContext();

  const { trackEvent } = useTrackEvent();
  const { respondToTeamInvite } = useRespondToInvite();
  const { showToast } = useToast();

  const [state, dispatchTeams] = useReducer(reducerTeams, {
    isLoading: false,
    teamName: "",
    description: "",
    isMemberInputValid: false,
    selectedMembersIds: [],
  });

  /** Determines if the team name is valid */
  const isValidName = isValidTeamName(state.teamName);

  /**
   * Whether the create button should be disabled or not.
   * The button should be disabled if:
   * - The team name is empty
   * - The team name isn't valid
   * - The number of selected members exceeds the maximum allowed for invitation.
   *  Note: Selecting members is optional. However, if members are selected, their count must be valid
   *  (i.e., within the allowed range).
   */
  const isCreateDisabled =
    (state.selectedMembersIds.length > 0 &&
      shouldDisableSendInvite(
        state.selectedMembersIds,
        state.isMemberInputValid
      )) ||
    !isValidName ||
    state.teamName.trim().length < 1 ||
    state.teamName.trim().length > MAX_NAME_CHARACTERS ||
    state.description.trim().length > MAX_DESCRIPTION_CHARACTERS ||
    ((state.selectedRole === "COMPANY_MANAGER" ||
      state.selectedRole === "PROJECT_MANAGER") &&
      !state.selectedGroupId);

  function resetStates(): void {
    dispatchTeams({ type: "SET_TEAM_NAME", teamName: "" });
    dispatchTeams({ type: "SET_MESSAGE", message: undefined });
    dispatchTeams({ type: "SET_DESCRIPTION", description: "" });
    dispatchTeams({
      type: "SET_SELECTED_GROUP_ID",
      selectedGroupId: undefined,
    });
    dispatchTeams({ type: "SET_SELECTED_ROLE", selectedRole: undefined });
    dispatchTeams({ type: "SET_SELECTED_MEMBERS_IDS", selectedMembersIds: [] });
  }

  /** Triggered when the create button is clicked, creates the group and closes the dialog */
  async function handleConfirm(): Promise<void> {
    dispatchTeams({ type: "SET_IS_LOADING", isLoading: true });

    trackEvent({
      name: TeamEvents.createNewTeam,
      props: { numberOfMembers: state.selectedMembersIds.length },
    });

    try {
      // Create Team
      const team = await dispatch(
        createTeam({
          companyId,
          teamName: state.teamName.trim(),
          description: state.description.trim(),
        })
      ).unwrap();

      const addNewTeamMemberStore = convertToTeamMemberTypes(
        team,
        state.selectedRole
      );

      dispatch(setOne(addNewTeamMemberStore));

      // Assign a role only if a role is selected
      if (state.selectedRole) {
        if (state.selectedGroupId && isGroupRole(state.selectedRole)) {
          // Assign team as a member to a group with group manager or project manager role
          await dispatch(
            updateMemberRoleInGroup({
              companyId,
              groupId: state.selectedGroupId,
              role: state.selectedRole,
              userIdentity: team.identity,
            })
          );
        } else {
          // Assign member to the company with workspace role
          await dispatch(
            updateMemberRoleInWorkspace({
              companyId,
              role: state.selectedRole as FaroCoreApiTypes.UpdateCompanyMemberRole,
              identity: team.identity,
            })
          );
        }
      }

      dispatchTeams({ type: "SET_TEAM_NAME", teamName: state.teamName.trim() });

      /** Team creation success message */
      const responseMessage = `${capitalizeFirstLetter(
        TEAM_DISPLAY_NAME
      )} created successfully`;

      /** Check if there are selected members to add; if so, proceed to add them, otherwise show a success message. */
      if (state.selectedMembersIds.length > 0) {
        await handleAddMembers(team.id, responseMessage);
      } else {
        showToast({
          type: "success",
          message: responseMessage,
        });
      }

      onCloseDialog();
      resetStates();
    } finally {
      dispatchTeams({ type: "SET_IS_LOADING", isLoading: false });
    }
  }

  /**
   * Add the members to the team
   * @param teamId The id of the team
   * @param responseMessage The message to be displayed after adding the members
   * @returns Promise<void>
   */
  async function handleAddMembers(
    teamId: TeamId,
    responseMessage: string
  ): Promise<void> {
    if (!companyId) {
      throw new Error("companyId is required");
    }
    try {
      // Add members
      const response = await dispatch(
        addMembersToTeam({
          companyId,
          teamId,
          members: state.selectedMembersIds,
        })
      ).unwrap();

      respondToTeamInvite(response, state.selectedMembersIds.length);
    } catch (error) {
      handleErrorWithToast({
        id: `inviteMemberToTeam-${Date.now().toString()}`,
        title: `${responseMessage}. But couldn't invite the ${nounPluralize({
          shouldShowCounter: false,
          counter: state.selectedMembersIds.length,
          word: "member",
        })}`,
        error,
      });
    }
  }

  return (
    <Dialog
      title={`Create new ${TEAM_DISPLAY_NAME}`}
      open={isDialogOpen}
      confirmText="Create"
      isConfirmDisabled={isCreateDisabled}
      onConfirm={handleConfirm}
      showSpinner={state.isLoading}
      onCancel={() => {
        onCloseDialog();
        resetStates();
      }}
    >
      <CreateTeamDialogContent state={state} dispatchTeams={dispatchTeams} />
    </Dialog>
  );
}
